import React, { useState } from 'react';
import { useLoadedUser } from '../../../hooks/api/useUser';
import UserRegistrationForm from './UserRegistrationForm';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { View } from 'react-native';
import { APITypes, Dynamo } from '../../../primary-record-types';
import UserIntentsForm from './UserIntentsForm';
import { RegisterUserArgs } from './useUserOnboardingFlow';

export type UserInfoScreenOwnProps = {
    isLoading: boolean;
    onRegister: (args: RegisterUserArgs) => void;
    defaultUserType: Dynamo.UserType | null;
    skipUserIntents: boolean;
};

export type UserInfoScreenProps = UserInfoScreenOwnProps;

const UserRegistration: React.FC<UserInfoScreenProps> = ({
    isLoading,
    onRegister,
    defaultUserType,
    skipUserIntents,
}) => {
    const userType = defaultUserType || 'consumer';
    const { user } = useLoadedUser({});
    const [updatedUser, setUpdatedUser] = useState<APITypes.userService.UpdateUserBody | null>();

    const renderContent = () => {
        if (!updatedUser) {
            // Initial user registration.  We will always hit this in the user registration flow.
            return (
                <UserRegistrationForm
                    user={user}
                    onUpdate={(updatedUser) => {
                        // if the user registers and we already know the user type is a professional user, we can skip the user type selection screen
                        if (userType === 'professional') {
                            onRegister({
                                user: updatedUser,
                                intents: ['professional-use'],
                                type: 'professional',
                            });
                        } else if (skipUserIntents) {
                            onRegister({
                                user: updatedUser,
                                type: 'consumer',
                            });
                        } else {
                            setUpdatedUser(updatedUser);
                        }
                    }}
                />
            );
        } else {
            return (
                <UserIntentsForm
                    onSkip={() => onRegister({ user: updatedUser, type: userType })}
                    onSubmit={(userIntents) =>
                        onRegister({
                            user: updatedUser,
                            intents: userIntents,
                            type: userType,
                        })
                    }
                />
            );
        }
    };

    return (
        <View style={{ width: '100%', height: '100%' }} testID="user-registration-container">
            <AnimateLoadedContent isLoading={isLoading}>{renderContent}</AnimateLoadedContent>
        </View>
    );
};

export default React.memo(UserRegistration);
